import React from "react";
import { Avatar as MuiAvatar, AvatarProps, styled } from "@mui/material";
import Color from "color";

import { contrastColor, stringToColor } from "fond/utils/colors";

interface IProps extends AvatarProps {
  /**
   * Defined the maximum intials used from the name prop.
   */
  maxInitials?: number;
  /**
   * The name used to determine the initials if children have not been passed
   */
  name?: string;
  /**
   * If true the avatar background color will be determined from the name prop.
   */
  stringColor?: boolean;
}

/**
 * Helper function that returns the initials of the passed name.  If a maxInitials is
 * passed the value will be limited to the number set.
 */
function defaultInitials(name: string, maxInitials?: number) {
  return name
    .split(/\s/)
    .map((part) => part.substring(0, 1).toUpperCase())
    .filter((v) => !!v)
    .slice(0, maxInitials)
    .join("")
    .toUpperCase();
}

function stringAvatar(name: string) {
  const color = Color(stringToColor(name));
  const fontColor = contrastColor(color.hex());

  return {
    sx: {
      bgcolor: color.hex(),
      color: fontColor,
    },
  };
}

const Avatar: React.FC<IProps> = ({ children, maxInitials, name = "", stringColor = false, sx, ...rest }: IProps) => {
  const stringAvatarProps = stringColor ? stringAvatar(name) : null;
  const mergedSx = stringAvatarProps ? { ...stringAvatarProps.sx, ...sx } : sx;

  return (
    <MuiAvatar {...rest} sx={mergedSx}>
      {children || defaultInitials(name, maxInitials)}
    </MuiAvatar>
  );
};

export const MediumAvatar = styled(Avatar)(() => ({
  height: "34px",
  width: "34px",
}));

export default Avatar;
